// novem atom — icon-in-input search (NovemHR-ekranlar port).
//
// The "magnifier glyph sitting inside a rounded input" pattern was
// repeated across A2 (people/directory search), A3 (leave), and B2/B3/B4
// (operator filter bars). This promotes it: a search-box with a leading
// icon slot and an optional Escape-clear.
//
// Icon-agnostic: the leading glyph is a SLOT (`icon` prop) — the library
// does not depend on lucide-react; the host passes its icon element. The
// slot is aria-hidden (decorative); the accessible name comes from the
// caller's aria-label (a search field with no visible <label> needs one).
//
// Accessibility:
//   * type="search" → the input exposes role="searchbox".
//   * Leading icon is aria-hidden (decorative).
//   * Escape clears a non-empty value (dispatches a real input event so
//     controlled-component consumers see onChange) — matches the Input
//     search-variant keyboard model.

import {
  forwardRef,
  useCallback,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Leading icon element (decorative — host passes a lucide glyph). */
  readonly icon?: ReactNode;
  /** When true, Escape clears a non-empty value. Defaults to true. */
  readonly clearOnEscape?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ icon, clearOnEscape = true, className, onKeyDown, disabled, ...rest }, ref) => {
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (clearOnEscape && event.key === "Escape") {
          const el = event.currentTarget;
          if (el.value.length > 0) {
            event.preventDefault();
            // Native setter bypasses React's value-tracking so controlled
            // consumers still receive the onChange for the cleared value.
            const nativeSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              "value",
            )?.set;
            nativeSetter?.call(el, "");
            el.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
        onKeyDown?.(event);
      },
      [clearOnEscape, onKeyDown],
    );

    return (
      <div
        data-component="search-input"
        data-disabled={disabled === true ? true : undefined}
        className={cx("nai-search-input", className)}
      >
        {icon !== undefined && (
          <span className="nai-search-input-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <input
          ref={ref}
          type="search"
          data-has-icon={icon !== undefined ? true : undefined}
          disabled={disabled === true}
          className="nai-search-input-control"
          onKeyDown={handleKeyDown}
          {...rest}
        />
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";
